import type { InviteCreated, OrgRole, UserOrganization } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, RotateCw, Trash2, UserPlus } from 'lucide-react';
import { useState } from 'react';

import { CopyButton } from '@/components/copy-button';
import { ErrorState } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { canInvite, ROLES } from '@/lib/roles';

/**
 * Pending invitations, and the form that creates them. Admins only.
 *
 * The accept link is shown once, when an invitation is created or re-sent: the
 * server keeps only a hash of its token, so it cannot show the link again.
 */
export function Invitations({ organization }: { organization: UserOrganization }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [delivered, setDelivered] = useState<InviteCreated | null>(null);

  const invites = useQuery({
    queryKey: ['invites', organization.id],
    queryFn: () => client.listInvites(organization.id),
  });
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['invites', organization.id] });

  const resend = useMutation({
    mutationFn: (inviteId: string) => client.resendInvite(organization.id, inviteId),
    onSuccess: (result) => {
      setDelivered(result);
      invalidate();
    },
  });
  const revoke = useMutation({
    mutationFn: (inviteId: string) => client.revokeInvite(organization.id, inviteId),
    onSuccess: () => {
      setDelivered(null);
      invalidate();
    },
  });
  const error = resend.error ?? revoke.error;

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="size-4" />
            Invitations
          </CardTitle>
          <CardDescription>
            An invitation link works once and expires after seven days.
          </CardDescription>
        </div>
        <InviteDialog
          organization={organization}
          onCreated={(result) => {
            setDelivered(result);
            invalidate();
          }}
        />
      </CardHeader>
      <CardContent className="space-y-3">
        {delivered && <DeliveredLink delivered={delivered} />}

        {invites.isError && (
          <ErrorState error={invites.error} onRetry={() => void invites.refetch()} />
        )}
        {invites.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">No pending invitations.</p>
        )}

        {invites.data?.map((invite) => (
          <div
            key={invite.id}
            className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="min-w-0 truncate text-sm font-medium">{invite.email}</span>
                <Badge variant="secondary">{invite.role}</Badge>
                {invite.expired && <Badge variant="muted">expired</Badge>}
              </div>
              <span className="block text-xs text-muted-foreground">
                {invite.inviter_name ? `Invited by ${invite.inviter_name} · ` : ''}
                {invite.expired ? 'expired' : 'expires'}{' '}
                {new Date(invite.expires_at).toLocaleDateString()}
              </span>
            </div>
            {canInvite(organization.role, invite.role) && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  title={`Send ${invite.email} a new link`}
                  onClick={() => resend.mutate(invite.id)}
                  disabled={resend.isPending}
                >
                  <RotateCw />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title={`Revoke the invitation for ${invite.email}`}
                  onClick={() => revoke.mutate(invite.id)}
                  disabled={revoke.isPending}
                >
                  <Trash2 />
                </Button>
              </>
            )}
          </div>
        ))}

        {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}

/** Say whether the invitee was emailed, and hand over the link either way. */
function DeliveredLink({ delivered }: { delivered: InviteCreated }) {
  return (
    <div className="space-y-2 rounded-md border border-primary bg-brand-subtle p-3">
      <p className="text-sm">
        {delivered.email_sent
          ? `We emailed ${delivered.invite.email} an invitation. You can also share the link yourself:`
          : `No email was sent: this installation has no working mail server. Share this link with ${delivered.invite.email}:`}
      </p>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={delivered.accept_url}
          onFocus={(event) => event.currentTarget.select()}
          className="font-mono text-xs"
          aria-label="Invitation link"
        />
        <CopyButton value={delivered.accept_url} />
      </div>
      <p className="text-xs text-muted-foreground">
        This link is shown only once. Re-send the invitation to get a new one.
      </p>
    </div>
  );
}

function InviteDialog({
  organization,
  onCreated,
}: {
  organization: UserOrganization;
  onCreated: (result: InviteCreated) => void;
}) {
  const { client } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [projectIds, setProjectIds] = useState<string[]>([]);

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => client.listProjects(),
    enabled: open,
  });
  const orgProjects = projects.data?.filter((project) => project.org_id === organization.id) ?? [];
  const roles = ROLES.filter((candidate) => canInvite(organization.role, candidate));

  const create = useMutation({
    mutationFn: () =>
      client.createInvite(organization.id, {
        email,
        role,
        // Admins and owners see every project, so a selection would mean nothing.
        project_ids: role === 'member' ? projectIds : [],
      }),
    onSuccess: (result) => {
      onCreated(result);
      setOpen(false);
      setEmail('');
      setRole('member');
      setProjectIds([]);
    },
  });

  const toggle = (projectId: string) =>
    setProjectIds((current) =>
      current.includes(projectId)
        ? current.filter((id) => id !== projectId)
        : [...current, projectId],
    );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">
          <UserPlus />
          Invite
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite someone to {organization.name}</DialogTitle>
          <DialogDescription>
            They get a link to create an account, or to sign in if they already have one.
          </DialogDescription>
        </DialogHeader>

        <form
          id="invite-form"
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="colleague@example.com"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={(value) => setRole(value as OrgRole)}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((candidate) => (
                  <SelectItem key={candidate} value={candidate}>
                    {candidate}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {role === 'member'
                ? 'Members only see the projects you choose.'
                : 'Admins and owners can access every project.'}
            </p>
          </div>

          {role === 'member' && (
            <fieldset className="space-y-1.5">
              <legend className="text-sm font-medium">Projects</legend>
              {projects.data && orgProjects.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  This organization has no projects yet.
                </p>
              )}
              {orgProjects.map((project) => (
                <label key={project.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4 accent-control"
                    checked={projectIds.includes(project.id)}
                    onChange={() => toggle(project.id)}
                  />
                  {project.name}
                </label>
              ))}
            </fieldset>
          )}

          {create.error && (
            <p className="text-sm text-destructive">{(create.error as Error).message}</p>
          )}
        </form>

        <DialogFooter>
          <Button type="submit" form="invite-form" disabled={!email.trim() || create.isPending}>
            Create invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
